import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  Campaign,
  CampaignType,
  TriggerType,
  SegmentRule,
  InlineTemplate,
  Schedule,
  Trigger,
  fetchCampaign,
  createCampaign,
  updateCampaign,
  uploadCampaignImage,
  CAMPAIGN_LANGUAGES,
  CampaignLanguage,
  CampaignTranslations,
  TRANSLATION_TITLE_MAX,
  TRANSLATION_BODY_MAX,
  hasTranslations,
  saveCampaignTranslations,
} from '../services/campaignApi';

// Only rows with a title or a body are sent; a half-filled row is an error.
const cleanTranslations = (
  translations: CampaignTranslations
): { clean: CampaignTranslations; incomplete: string[] } => {
  const clean: CampaignTranslations = {};
  const incomplete: string[] = [];
  for (const { code, label } of CAMPAIGN_LANGUAGES) {
    const row = translations[code];
    const title = row?.title.trim() ?? '';
    const body = row?.body.trim() ?? '';
    if (!title && !body) continue;
    if (!title || !body) {
      incomplete.push(label);
      continue;
    }
    clean[code] = { title, body };
  }
  return { clean, incomplete };
};

// ============================================================
// NOTIFICATION TEMPLATES
// ============================================================
const NOTIFICATION_TEMPLATES = {
  WEEKLY_WINBACK: {
    id: 'WEEKLY_WINBACK',
    name: 'Weekly Winback',
    category: 'Re-engagement',
    icon: '🔄',
    color: 'from-purple-500 to-pink-500',
    title: "We miss you! 🌟",
    body: "Your followers are waiting! Come back and see what's new on Tezda.",
    actionType: 'OPEN_APP',
    triggerType: 'USER_INACTIVE' as TriggerType,
    audienceFilters: [{ field: 'LastActiveTime', operator: 'LESS_THAN', value: '7d' }],
    description: 'Bring back users who have been inactive for 7+ days',
    stats: { avgCtr: '4.2%', avgOpen: '18%' },
  },
  STORY_NEW_REMINDER: {
    id: 'STORY_NEW_REMINDER',
    name: 'New Stories Alert',
    category: 'Engagement',
    icon: '📖',
    color: 'from-blue-500 to-cyan-500',
    title: "New stories from people you follow! 📖",
    body: "{count} people you follow have posted new stories. Check them out!",
    actionType: 'OPEN_APP',
    triggerType: 'STORY_CREATED' as TriggerType,
    audienceFilters: [],
    description: 'Notify users about new stories from followed accounts',
    stats: { avgCtr: '6.8%', avgOpen: '24%' },
  },
  STORY_EXPIRING: {
    id: 'STORY_EXPIRING',
    name: 'Expiring Stories',
    category: 'Urgency',
    icon: '⏰',
    color: 'from-orange-500 to-red-500',
    title: "Stories expiring soon! ⏰",
    body: "{count} stories from people you follow are about to expire. Watch them now!",
    actionType: 'OPEN_APP',
    triggerType: 'STORY_EXPIRING_SOON' as TriggerType,
    audienceFilters: [],
    description: 'Create urgency with expiring content alerts',
    stats: { avgCtr: '8.1%', avgOpen: '28%' },
  },
  PING_MISSED_RECAP: {
    id: 'PING_MISSED_RECAP',
    name: 'Missed Pings',
    category: 'Re-engagement',
    icon: '🔔',
    color: 'from-yellow-500 to-orange-500',
    title: "Pings you might have missed 🔔",
    body: "You have {count} unviewed pings waiting for you!",
    actionType: 'OPEN_APP',
    triggerType: 'USER_INACTIVE' as TriggerType,
    audienceFilters: [{ field: 'LastActiveTime', operator: 'LESS_THAN', value: '24h' }],
    description: 'Recap of missed content for returning users',
    stats: { avgCtr: '5.5%', avgOpen: '22%' },
  },
  TRENDING_VIDEO: {
    id: 'TRENDING_VIDEO',
    name: 'Trending Content',
    category: 'Discovery',
    icon: '🔥',
    color: 'from-red-500 to-orange-500',
    title: "Trending now 🔥",
    body: "A video you might like is going viral. Check it out!",
    actionType: 'OPEN_VIDEO',
    triggerType: 'PING_TRENDING' as TriggerType,
    audienceFilters: [],
    description: 'Promote trending videos to boost engagement',
    stats: { avgCtr: '7.2%', avgOpen: '26%' },
  },
  UNREAD_DMS: {
    id: 'UNREAD_DMS',
    name: 'Unread Messages',
    category: 'Communication',
    icon: '💬',
    color: 'from-green-500 to-emerald-500',
    title: "You have unread messages 💬",
    body: "{count} messages are waiting for you. Don't keep them waiting!",
    actionType: 'OPEN_APP',
    triggerType: 'DM_UNREAD_PENDING' as TriggerType,
    audienceFilters: [],
    description: 'Remind users about pending messages',
    stats: { avgCtr: '12.3%', avgOpen: '35%' },
  },
  ENGAGEMENT_RECAP: {
    id: 'ENGAGEMENT_RECAP',
    name: 'Engagement Summary',
    category: 'Re-engagement',
    icon: '🎉',
    color: 'from-pink-500 to-rose-500',
    title: "Your content is getting attention! 🎉",
    body: "You've received {likes} likes and {comments} comments while you were away.",
    actionType: 'OPEN_APP',
    triggerType: 'USER_INACTIVE' as TriggerType,
    audienceFilters: [{ field: 'LastActiveTime', operator: 'LESS_THAN', value: '3d' }],
    description: 'Show engagement stats to inactive creators',
    stats: { avgCtr: '9.4%', avgOpen: '32%' },
  },
  NEW_USER_WELCOME: {
    id: 'NEW_USER_WELCOME',
    name: 'New User Welcome',
    category: 'Onboarding',
    icon: '👋',
    color: 'from-indigo-500 to-purple-500',
    title: "Welcome to Tezda! 👋",
    body: "Start by watching this trending video picked just for you.",
    actionType: 'OPEN_VIDEO',
    triggerType: 'USER_NEW' as TriggerType,
    audienceFilters: [],
    description: 'Welcome new users with curated content',
    stats: { avgCtr: '15.2%', avgOpen: '42%' },
  },
  MILESTONE_1K: {
    id: 'MILESTONE_1K',
    name: '1K Views Milestone',
    category: 'Celebration',
    icon: '🎊',
    color: 'from-amber-500 to-yellow-500',
    title: "Congratulations! 🎊",
    body: "Your ping just hit 1,000 views! Keep creating amazing content.",
    actionType: 'OPEN_VIDEO',
    triggerType: 'PING_MILESTONE' as TriggerType,
    audienceFilters: [],
    description: 'Celebrate creator milestones',
    stats: { avgCtr: '11.8%', avgOpen: '38%' },
  },
  MILESTONE_1M: {
    id: 'MILESTONE_1M',
    name: '1M Views Milestone',
    category: 'Celebration',
    icon: '🚀',
    color: 'from-violet-500 to-purple-500',
    title: "You're going viral! 🚀",
    body: "Your ping just hit 1,000,000 views! You're officially viral!",
    actionType: 'OPEN_VIDEO',
    triggerType: 'PING_MILESTONE' as TriggerType,
    audienceFilters: [],
    description: 'Celebrate major viral moments',
    stats: { avgCtr: '18.5%', avgOpen: '52%' },
  },
  CUSTOM: {
    id: 'CUSTOM',
    name: 'Custom Campaign',
    category: 'Custom',
    icon: '✏️',
    color: 'from-slate-500 to-gray-600',
    title: "",
    body: "",
    actionType: '',
    triggerType: 'USER_INACTIVE' as TriggerType,
    audienceFilters: [],
    description: 'Create your own custom notification',
    stats: { avgCtr: '-', avgOpen: '-' },
  },
};

type TemplateKey = keyof typeof NOTIFICATION_TEMPLATES;

// ============================================================
// WIZARD STEPS
// ============================================================
const STEPS = [
  { id: 1, name: 'Template', icon: '📋' },
  { id: 2, name: 'Content', icon: '✍️' },
  { id: 3, name: 'Audience', icon: '👥' },
  { id: 4, name: 'Schedule', icon: '📅' },
  { id: 5, name: 'Review', icon: '✅' },
];

// ============================================================
// DEVICE PREVIEW COMPONENT
// ============================================================
const DevicePreview: React.FC<{ title: string; body: string; platform: 'ios' | 'android'; imageUrl?: string }> = ({ title, body, platform, imageUrl }) => {
  const time = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  if (platform === 'ios') {
    return (
      <div className="bg-gradient-to-b from-gray-300 to-gray-400 rounded-[2.5rem] p-2 w-64 shadow-2xl">
        {/* iPhone frame */}
        <div className="bg-black rounded-[2.2rem] overflow-hidden relative">
          {/* Dynamic Island */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20" />

          {/* Lock screen wallpaper */}
          <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 min-h-[520px] p-4 pt-10">
            {/* Status bar */}
            <div className="flex justify-between items-center text-white text-xs px-4">
              <span className="font-medium">{time}</span>
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3c-1.5 0-2.9.5-4 1.5l1.4 1.4c.8-.6 1.7-.9 2.6-.9s1.8.3 2.6.9l1.4-1.4c-1.1-1-2.5-1.5-4-1.5zm-6 3.5l1.4 1.4c1.2-1.2 2.9-2 4.6-2s3.4.7 4.6 2l1.4-1.4c-1.6-1.6-3.7-2.5-6-2.5s-4.4.9-6 2.5zM12 9c-1.1 0-2.1.5-2.8 1.2l1.4 1.4c.4-.4.9-.6 1.4-.6s1 .2 1.4.6l1.4-1.4c-.7-.7-1.7-1.2-2.8-1.2zm0 3c-.6 0-1 .4-1 1s.4 1 1 1 1-.4 1-1-.4-1-1-1z"/></svg>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17 4h-3V2h-4v2H7v18h10V4zm-4 16h-2v-2h2v2zm0-4h-2V8h2v8z"/></svg>
              </div>
            </div>

            {/* Date/Time display */}
            <div className="text-center mt-8 mb-6">
              <p className="text-white/60 text-sm">Monday, February 9</p>
              <p className="text-white text-5xl font-light mt-1">{time}</p>
            </div>

            {/* iOS Notification */}
            <div className="mx-1 mt-4">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl overflow-hidden shadow-xl">
                {/* Notification header */}
                <div className="px-4 pt-3 pb-2 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF9C15] to-[#F42026] flex items-center justify-center">
                    <span className="text-white font-bold text-sm">P</span>
                  </div>
                  <span className="text-gray-600 text-xs font-medium uppercase tracking-wide">PINGTOP</span>
                  <span className="text-gray-400 text-xs ml-auto">now</span>
                </div>

                {/* Notification content */}
                <div className="px-4 pb-3">
                  <p className="font-semibold text-gray-900 text-sm">
                    {title || 'Notification Title'}
                  </p>
                  <p className="text-gray-600 text-xs mt-0.5 line-clamp-2">
                    {body || 'Your notification message will appear here...'}
                  </p>
                </div>

                {/* Rich media attachment (iOS style) */}
                {imageUrl && (
                  <div className="px-3 pb-3">
                    <img
                      src={imageUrl}
                      alt="Notification"
                      className="w-full h-32 object-cover rounded-xl"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Home indicator */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
              <div className="w-28 h-1 bg-white/40 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android Material You style
  return (
    <div className="bg-gray-800 rounded-3xl p-2 w-64 shadow-2xl">
      <div className="bg-gray-900 rounded-[1.8rem] overflow-hidden relative min-h-[520px]">
        {/* Status bar */}
        <div className="flex justify-between items-center text-white/70 text-xs px-5 py-2">
          <span className="font-medium">{time}</span>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3c-1.5 0-2.9.5-4 1.5l1.4 1.4c.8-.6 1.7-.9 2.6-.9s1.8.3 2.6.9l1.4-1.4c-1.1-1-2.5-1.5-4-1.5zm-6 3.5l1.4 1.4c1.2-1.2 2.9-2 4.6-2s3.4.7 4.6 2l1.4-1.4c-1.6-1.6-3.7-2.5-6-2.5s-4.4.9-6 2.5zM12 9c-1.1 0-2.1.5-2.8 1.2l1.4 1.4c.4-.4.9-.6 1.4-.6s1 .2 1.4.6l1.4-1.4c-.7-.7-1.7-1.2-2.8-1.2zm0 3c-.6 0-1 .4-1 1s.4 1 1 1 1-.4 1-1-.4-1-1-1z"/></svg>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17 4h-3V2h-4v2H7v18h10V4zm-4 16h-2v-2h2v2zm0-4h-2V8h2v8z"/></svg>
          </div>
        </div>

        {/* Lock screen content area */}
        <div className="px-4 pt-4">
          {/* Date/Time display (Android style) */}
          <div className="text-center mb-6">
            <p className="text-white text-5xl font-light">{time}</p>
            <p className="text-white/60 text-sm mt-1">Mon, Feb 9</p>
          </div>

          {/* Android Notification (Material You style) */}
          <div className="bg-[#2d2d2d] rounded-2xl overflow-hidden shadow-lg">
            {/* Notification header row */}
            <div className="px-4 py-2 flex items-center gap-2 border-b border-white/5">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">P</span>
              </div>
              <span className="text-white/70 text-xs">PingTop</span>
              <span className="text-white/40 text-xs ml-auto">now</span>
              <svg className="w-4 h-4 text-white/40" fill="currentColor" viewBox="0 0 24 24"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>
            </div>

            {/* Notification content */}
            <div className="p-4">
              <p className="font-medium text-white text-sm">
                {title || 'Notification Title'}
              </p>
              <p className="text-white/60 text-xs mt-1 line-clamp-2">
                {body || 'Your notification message will appear here...'}
              </p>

              {/* Big Picture style for Android */}
              {imageUrl && (
                <div className="mt-3 -mx-4 -mb-4">
                  <img
                    src={imageUrl}
                    alt="Notification"
                    className="w-full h-28 object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Android gesture bar */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
          <div className="w-24 h-1 bg-white/30 rounded-full" />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const CampaignBuilder: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const duplicateData = location.state?.duplicateFrom as Campaign | undefined;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState<'ios' | 'android'>('ios');

  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateKey | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<CampaignType>('ONE_TIME');
  const [targetPlatforms, setTargetPlatforms] = useState<string[]>(['all']);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [priority, _setPriority] = useState(0);

  // Content
  const [templateTitle, setTemplateTitle] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateImageUrl, setTemplateImageUrl] = useState('');
  const [templateActionType, setTemplateActionType] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Per-language push text (optional). `hadTranslations` lets an edit clear them.
  const [translations, setTranslations] = useState<CampaignTranslations>({});
  const [hadTranslations, setHadTranslations] = useState(false);
  const [showTranslations, setShowTranslations] = useState(false);

  const setTranslationField = (code: CampaignLanguage, field: 'title' | 'body', value: string) => {
    setTranslations((prev) => ({
      ...prev,
      [code]: { title: prev[code]?.title ?? '', body: prev[code]?.body ?? '', [field]: value },
    }));
  };

  // Schedule
  const [scheduleType, setScheduleType] = useState<'NOW' | 'SCHEDULED'>('SCHEDULED');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleTimezone, setScheduleTimezone] = useState('UTC');
  const [scheduleDate, setScheduleDate] = useState('');

  // Trigger
  const [triggerType, setTriggerType] = useState<TriggerType>('USER_INACTIVE');

  // Audience
  const [audienceType, setAudienceType] = useState<'ALL' | 'SEGMENT' | 'USERS'>('ALL');
  const [userIds, setUserIds] = useState('');
  const [segmentFilters, setSegmentFilters] = useState<Array<{ field: string; operator: string; value: string }>>([]);
  const [segmentOperator, setSegmentOperator] = useState<'AND' | 'OR'>('AND');

  // Load campaign if editing or duplicating
  useEffect(() => {
    if (isEditing && id) {
      loadCampaignData(id);
    } else if (duplicateData) {
      // Pre-fill form with duplicated campaign data
      loadDuplicateData(duplicateData);
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduleDate(tomorrow.toISOString().split('T')[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, duplicateData]);

  const loadDuplicateData = (campaign: Campaign) => {
    setName(campaign.name); // User can change this
    setDescription(campaign.description || '');
    setType(campaign.type);
    if (campaign.targetPlatforms) {
      setTargetPlatforms(campaign.targetPlatforms as string[]);
    }
    if (campaign.inlineTemplate) {
      setTemplateTitle(campaign.inlineTemplate.title || '');
      setTemplateBody(campaign.inlineTemplate.body || '');
      setTemplateImageUrl(campaign.inlineTemplate.imageUrl || '');
      setTemplateActionType(campaign.inlineTemplate.actionType || '');
    }
    if (hasTranslations(campaign.translations)) {
      setTranslations(campaign.translations || {});
      setShowTranslations(true);
    }
    if (campaign.schedule) {
      // For duplicated campaigns: default to "Send Now" if the original was completed
      // This is the common use case - user wants to re-send a similar campaign
      const isCompleted = campaign.status === 'COMPLETED';
      if (campaign.schedule.sendNow || isCompleted) {
        setScheduleType('NOW');
      } else {
        setScheduleType('SCHEDULED');
        setScheduleTime(campaign.schedule.time);
        setScheduleTimezone(campaign.schedule.timezone || 'UTC');
        // Set to tomorrow for duplicated campaigns
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setScheduleDate(tomorrow.toISOString().split('T')[0]);
      }
    }
    if (campaign.trigger) {
      setTriggerType(campaign.trigger.type);
    }
    if (campaign.segmentRule) {
      // Preserve the segment rule operator (AND/OR)
      if (campaign.segmentRule.operator) {
        setSegmentOperator(campaign.segmentRule.operator as 'AND' | 'OR');
      }
      if (campaign.segmentRule.filters) {
        // Check if this is a "Specific Users" campaign (only UserID EQUALS filters)
        const isSpecificUsers = campaign.segmentRule.filters.every(
          f => f.field === 'UserID' && f.operator === 'EQUALS'
        );

        if (isSpecificUsers) {
          // Extract user IDs and set as "Specific Users" audience type
          const userIdList = campaign.segmentRule.filters.map(f => String(f.value)).join(', ');
          setUserIds(userIdList);
          setAudienceType('USERS');
        } else {
          // It's a segment with other filters
          setSegmentFilters(campaign.segmentRule.filters.map(f => ({
            field: f.field,
            operator: f.operator,
            // Handle array values (from IN operator) - convert to comma-separated string
            value: Array.isArray(f.value) ? f.value.join(', ') : String(f.value)
          })));
          setAudienceType('SEGMENT');
        }
      }
    }
    // Start at content step since user likely wants to review/modify
    setCurrentStep(2);
    toast.info('Campaign duplicated - review and make changes');
  };

  const loadCampaignData = async (campaignId: string) => {
    setLoading(true);
    try {
      const campaign = await fetchCampaign(campaignId);
      setName(campaign.name);
      setDescription(campaign.description || '');
      setType(campaign.type);
      if (campaign.inlineTemplate) {
        setTemplateTitle(campaign.inlineTemplate.title || '');
        setTemplateBody(campaign.inlineTemplate.body || '');
        setTemplateImageUrl(campaign.inlineTemplate.imageUrl || '');
        setTemplateActionType(campaign.inlineTemplate.actionType || '');
      }
      if (hasTranslations(campaign.translations)) {
        setTranslations(campaign.translations || {});
        setHadTranslations(true);
        setShowTranslations(true);
      }
      if (campaign.schedule) {
        setScheduleTime(campaign.schedule.time);
        setScheduleTimezone(campaign.schedule.timezone || 'UTC');
        setScheduleDate(campaign.schedule.startDate?.split('T')[0] || '');
      }
      if (campaign.trigger) {
        setTriggerType(campaign.trigger.type);
      }
      if (campaign.segmentRule) {
        // Preserve the segment rule operator (AND/OR)
        if (campaign.segmentRule.operator) {
          setSegmentOperator(campaign.segmentRule.operator as 'AND' | 'OR');
        }
        if (campaign.segmentRule.filters) {
          // Check if this is a "Specific Users" campaign (only UserID EQUALS filters)
          const isSpecificUsers = campaign.segmentRule.filters.every(
            f => f.field === 'UserID' && f.operator === 'EQUALS'
          );

          if (isSpecificUsers) {
            // Extract user IDs and set as "Specific Users" audience type
            const userIdList = campaign.segmentRule.filters.map(f => String(f.value)).join(', ');
            setUserIds(userIdList);
            setAudienceType('USERS');
          } else {
            // It's a segment with other filters
            setSegmentFilters(campaign.segmentRule.filters.map(f => ({
              field: f.field,
              operator: f.operator,
              // Handle array values (from IN operator) - convert to comma-separated string
              value: Array.isArray(f.value) ? f.value.join(', ') : String(f.value)
            })));
            setAudienceType('SEGMENT');
          }
        }
      }
      setCurrentStep(5); // Go to review
    } catch (err: any) {
      toast.error(err.message);
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateKey: TemplateKey) => {
    setSelectedTemplate(templateKey);
    const template = NOTIFICATION_TEMPLATES[templateKey];
    setTemplateTitle(template.title);
    setTemplateBody(template.body);
    setTemplateActionType(template.actionType);
    setDescription(template.description);
    setTriggerType(template.triggerType);
    if (template.audienceFilters.length > 0) {
      setSegmentFilters(template.audienceFilters);
      setAudienceType('SEGMENT');
    }
    if (!name) {
      setName(template.name + ' Campaign');
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    if (!templateTitle.trim() || !templateBody.trim()) {
      toast.error('Notification title and body are required');
      return;
    }
    const { clean: cleanedTranslations, incomplete } = cleanTranslations(translations);
    if (incomplete.length > 0) {
      toast.error(`Add both a title and a body (or clear both) for: ${incomplete.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      const inlineTemplate: InlineTemplate = {
        title: templateTitle,
        body: templateBody,
        imageUrl: templateImageUrl || undefined,
        actionType: templateActionType || undefined,
      };

      let schedule: Schedule | undefined;
      if (type !== 'TRIGGERED') {
        schedule = {
          type: 'FIXED_TIMEZONE',
          timezone: scheduleTimezone,
          time: scheduleTime,
          startDate: scheduleType === 'NOW' ? new Date().toISOString() : new Date(scheduleDate).toISOString(),
          sendNow: scheduleType === 'NOW',
        };
      }

      const trigger: Trigger | undefined = type === 'TRIGGERED' ? { type: triggerType } : undefined;

      let segmentRule: SegmentRule | undefined;
      if (audienceType === 'USERS' && userIds.trim()) {
        segmentRule = {
          operator: 'OR',
          filters: userIds.split(',').map(id => ({
            field: 'UserID',
            operator: 'EQUALS' as const,
            value: id.trim()
          }))
        };
      } else if (audienceType === 'SEGMENT' && segmentFilters.length > 0) {
        segmentRule = {
          operator: segmentOperator, // Preserve the original operator (AND/OR)
          filters: segmentFilters.filter(f => f.field && f.value).map(f => ({
            field: f.field,
            operator: f.operator as any,
            // For IN/NOT_IN operators, convert comma-separated values to array
            value: ['IN', 'NOT_IN'].includes(f.operator)
              ? f.value.split(',').map((v: string) => v.trim()).filter(Boolean)
              : f.value
          }))
        };
      }

      // name and type are always set, so this is a complete create payload.
      const campaignData: Omit<Campaign, '_id' | 'status' | 'createdAt' | 'updatedAt'> = {
        name,
        description: description || undefined,
        type,
        targetPlatforms: targetPlatforms as any,
        inlineTemplate,
        schedule,
        trigger,
        segmentRule,
        priority,
      };

      let savedId: string | undefined;
      if (isEditing && id) {
        await updateCampaign(id, campaignData);
        savedId = id;
        toast.success('Campaign updated successfully');
      } else {
        const created = await createCampaign(campaignData);
        savedId = created._id;
        toast.success('Campaign created successfully');
      }

      // Per-language text is saved separately (V2 route). Sent when there is any, or to clear old ones.
      if (savedId && (hasTranslations(cleanedTranslations) || hadTranslations)) {
        try {
          await saveCampaignTranslations(savedId, cleanedTranslations);
          if (hasTranslations(cleanedTranslations)) {
            toast.success(`Translations saved: ${Object.keys(cleanedTranslations).length} languages`);
          }
        } catch (translationErr: unknown) {
          // The campaign itself is saved; open it for editing so the translations can be retried.
          const reason = translationErr instanceof Error ? translationErr.message : String(translationErr);
          toast.error(`Campaign saved, but translations were not: ${reason}`);
          navigate(`/campaigns/${savedId}/edit`);
          return;
        }
      }

      navigate('/campaigns');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedTemplate !== null;
      case 2: return templateTitle.trim() && templateBody.trim();
      case 3: return true;
      case 4: return scheduleType === 'NOW' || scheduleDate;
      default: return true;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF9C15]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/campaigns')}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {isEditing ? 'Edit Campaign' : 'Create Campaign'}
              </h1>
              <p className="text-gray-400 text-sm">Build engaging push notifications</p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => step.id <= currentStep && setCurrentStep(step.id)}
                  className={`flex flex-col items-center gap-2 transition-all ${
                    step.id === currentStep
                      ? 'text-white scale-110'
                      : step.id < currentStep
                      ? 'text-[#FF9C15] cursor-pointer hover:scale-105'
                      : 'text-gray-500'
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-xl transition-all ${
                      step.id === currentStep
                        ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] shadow-lg shadow-orange-500/30'
                        : step.id < currentStep
                        ? 'bg-[#FF9C15]/20 border-2 border-[#FF9C15]'
                        : 'bg-slate-700'
                    }`}
                  >
                    {step.id < currentStep ? '✓' : step.icon}
                  </div>
                  <span className="text-xs font-medium">{step.name}</span>
                </button>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 transition-colors ${
                      step.id < currentStep ? 'bg-[#FF9C15]' : 'bg-slate-700'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-8">
          {/* Left Panel - Form */}
          <div className="flex-1">
            {/* Step 1: Template Selection */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">Choose a Template</h2>
                  <p className="text-gray-400">Start with a proven template or create from scratch</p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.values(NOTIFICATION_TEMPLATES).map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id as TemplateKey)}
                      className={`group relative p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                        selectedTemplate === template.id
                          ? 'border-[#FF9C15] bg-[#FF9C15]/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                    >
                      {selectedTemplate === template.id && (
                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#FF9C15] flex items-center justify-center text-white text-sm">
                          ✓
                        </div>
                      )}
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center text-2xl mb-3`}>
                        {template.icon}
                      </div>
                      <h3 className="font-semibold text-white mb-1">{template.name}</h3>
                      <p className="text-xs text-gray-400 mb-2">{template.description}</p>
                      {template.stats.avgCtr !== '-' && (
                        <div className="flex gap-3 text-xs">
                          <span className="text-green-400">CTR: {template.stats.avgCtr}</span>
                          <span className="text-blue-400">Open: {template.stats.avgOpen}</span>
                        </div>
                      )}
                      <span className="absolute bottom-2 right-2 text-[10px] text-gray-500 bg-slate-800 px-2 py-0.5 rounded">
                        {template.category}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Content */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Campaign Details</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Campaign Name *</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                        placeholder="e.g., Summer Re-engagement Campaign"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Campaign Type</label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'ONE_TIME', label: 'One-Time', icon: '📤', desc: 'Send once' },
                          { value: 'RECURRING', label: 'Recurring', icon: '🔄', desc: 'Send repeatedly' },
                          { value: 'TRIGGERED', label: 'Triggered', icon: '⚡', desc: 'Event-based' },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setType(opt.value as CampaignType)}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                              type === opt.value
                                ? 'border-[#FF9C15] bg-[#FF9C15]/10'
                                : 'border-white/10 hover:border-white/20'
                            }`}
                          >
                            <span className="text-2xl">{opt.icon}</span>
                            <p className="font-medium text-white mt-2">{opt.label}</p>
                            <p className="text-xs text-gray-400">{opt.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Notification Content</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Title *</label>
                      <input
                        type="text"
                        value={templateTitle}
                        onChange={(e) => setTemplateTitle(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                        placeholder="Notification title"
                        maxLength={50}
                      />
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">Keep it short and engaging</span>
                        <span className="text-xs text-gray-500">{templateTitle.length}/50</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Body *</label>
                      <textarea
                        value={templateBody}
                        onChange={(e) => setTemplateBody(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                        placeholder="Notification message"
                        rows={3}
                        maxLength={150}
                      />
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">
                          Use {'{count}'}, {'{senderName}'}, {'{likes}'} for dynamic content
                        </span>
                        <span className="text-xs text-gray-500">{templateBody.length}/150</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Campaign Image (optional)</label>
                      <div className="space-y-3">
                        {templateImageUrl ? (
                          <div className="relative bg-slate-800 rounded-lg border border-white/10 p-2">
                            <img
                              src={templateImageUrl}
                              alt="Campaign preview"
                              className="w-full max-h-40 object-contain rounded"
                            />
                            <button
                              type="button"
                              onClick={() => setTemplateImageUrl('')}
                              className="absolute top-1 right-1 p-1.5 bg-red-500 hover:bg-red-600 rounded-full text-white"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-lg cursor-pointer bg-slate-800/50 hover:bg-slate-800 hover:border-[#FF9C15]/50 transition-all">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              {uploadingImage ? (
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF9C15]" />
                              ) : (
                                <>
                                  <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <p className="text-sm text-gray-400">Click to upload image</p>
                                  <p className="text-xs text-gray-500 mt-1">JPG/PNG, max 1MB, 1024x512 recommended</p>
                                </>
                              )}
                            </div>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/jpg"
                              className="hidden"
                              disabled={uploadingImage}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;

                                // Validate file size (1MB)
                                if (file.size > 1 * 1024 * 1024) {
                                  toast.error('Image must be less than 1MB');
                                  return;
                                }

                                // Validate file type
                                if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
                                  toast.error('Only JPG and PNG images are allowed');
                                  return;
                                }

                                setUploadingImage(true);
                                try {
                                  const result = await uploadCampaignImage(file);
                                  setTemplateImageUrl(result.imageUrl);
                                  toast.success('Image uploaded successfully');
                                } catch (err: any) {
                                  toast.error(err.message || 'Failed to upload image');
                                } finally {
                                  setUploadingImage(false);
                                  e.target.value = ''; // Reset input
                                }
                              }}
                            />
                          </label>
                        )}
                        <p className="text-xs text-gray-500">
                          2:1 aspect ratio works best for both iOS and Android notifications
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Action</label>
                      <select
                        value={templateActionType}
                        onChange={(e) => setTemplateActionType(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                      >
                        <option value="">None (Open app)</option>
                        <option value="OPEN_APP">Open App Home</option>
                        <option value="OPEN_VIDEO">Open Video</option>
                        <option value="OPEN_PROFILE">Open Profile</option>
                        <option value="OPEN_URL">Open URL</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <button
                    type="button"
                    onClick={() => setShowTranslations(!showTranslations)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <div>
                      <h2 className="text-xl font-semibold text-white">Translations (optional)</h2>
                      <p className="text-sm text-gray-400 mt-1">
                        A title and body per language. Each user gets their app language; everyone else gets the
                        text above. Used only by the Send Now button on the campaign page; scheduled runs
                        and Queue Run always send the default text.
                      </p>
                    </div>
                    <span className="text-gray-400 text-sm ml-4">
                      {Object.values(translations).filter((t) => t && (t.title.trim() || t.body.trim())).length}{' '}
                      filled {showTranslations ? '▲' : '▼'}
                    </span>
                  </button>
                  {showTranslations && (
                    <div className="space-y-4 mt-4">
                      {CAMPAIGN_LANGUAGES.map(({ code, label }) => (
                        <div key={code} className="border border-white/10 rounded-lg p-3">
                          <p className="text-sm font-medium text-white mb-2">
                            {label} <span className="text-gray-500">({code})</span>
                          </p>
                          <input
                            type="text"
                            value={translations[code]?.title ?? ''}
                            onChange={(e) => setTranslationField(code, 'title', e.target.value)}
                            className="w-full px-3 py-2 mb-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                            placeholder={`Title in ${label}`}
                            maxLength={TRANSLATION_TITLE_MAX}
                            dir={code === 'ar' || code === 'ur' ? 'rtl' : 'ltr'}
                          />
                          <textarea
                            value={translations[code]?.body ?? ''}
                            onChange={(e) => setTranslationField(code, 'body', e.target.value)}
                            className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                            placeholder={`Body in ${label}`}
                            rows={2}
                            maxLength={TRANSLATION_BODY_MAX}
                            dir={code === 'ar' || code === 'ur' ? 'rtl' : 'ltr'}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Audience */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Target Audience</h2>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {[
                      { value: 'ALL', label: 'All Users', icon: '🌍', desc: 'Send to everyone' },
                      { value: 'SEGMENT', label: 'User Segment', icon: '🎯', desc: 'Filter by criteria' },
                      { value: 'USERS', label: 'Specific Users', icon: '👤', desc: 'Enter user IDs' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAudienceType(opt.value as any)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          audienceType === opt.value
                            ? 'border-[#FF9C15] bg-[#FF9C15]/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <span className="text-2xl">{opt.icon}</span>
                        <p className="font-medium text-white mt-2">{opt.label}</p>
                        <p className="text-xs text-gray-400">{opt.desc}</p>
                      </button>
                    ))}
                  </div>

                  {audienceType === 'USERS' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">User IDs (comma-separated)</label>
                      <textarea
                        value={userIds}
                        onChange={(e) => setUserIds(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15] font-mono text-sm"
                        placeholder="user-123, user-456, user-789"
                        rows={4}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter user IDs separated by commas
                      </p>
                    </div>
                  )}

                  {audienceType === 'SEGMENT' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-400">Define who should receive this notification:</p>
                        <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
                          <button
                            type="button"
                            onClick={() => setSegmentOperator('AND')}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              segmentOperator === 'AND'
                                ? 'bg-[#FF9C15] text-black font-medium'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            Match ALL
                          </button>
                          <button
                            type="button"
                            onClick={() => setSegmentOperator('OR')}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              segmentOperator === 'OR'
                                ? 'bg-[#FF9C15] text-black font-medium'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            Match ANY
                          </button>
                        </div>
                      </div>
                      {segmentFilters.map((filter, index) => (
                        <div key={index} className="flex gap-2">
                          <select
                            value={filter.field}
                            onChange={(e) => {
                              const updated = [...segmentFilters];
                              updated[index].field = e.target.value;
                              setSegmentFilters(updated);
                            }}
                            className="flex-1 px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
                          >
                            <option value="">Select field</option>
                            <option value="UserID">User ID</option>
                            <option value="LastActiveTime">Last Active</option>
                            <option value="FollowersCount">Followers Count</option>
                            <option value="CountryCode">Country</option>
                            <option value="AccountStatus">Account Status</option>
                          </select>
                          <select
                            value={filter.operator}
                            onChange={(e) => {
                              const updated = [...segmentFilters];
                              updated[index].operator = e.target.value;
                              setSegmentFilters(updated);
                            }}
                            className="w-32 px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
                          >
                            <option value="EQUALS">equals</option>
                            <option value="NOT_EQUALS">not equals</option>
                            <option value="IN">in list</option>
                            <option value="NOT_IN">not in list</option>
                            <option value="GREATER_THAN">greater than</option>
                            <option value="LESS_THAN">less than</option>
                            <option value="CONTAINS">contains</option>
                          </select>
                          <input
                            type="text"
                            value={filter.value}
                            onChange={(e) => {
                              const updated = [...segmentFilters];
                              updated[index].value = e.target.value;
                              setSegmentFilters(updated);
                            }}
                            className="flex-1 px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
                            placeholder="Value"
                          />
                          <button
                            type="button"
                            onClick={() => setSegmentFilters(segmentFilters.filter((_, i) => i !== index))}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setSegmentFilters([...segmentFilters, { field: '', operator: 'EQUALS', value: '' }])}
                        className="flex items-center gap-2 text-[#FF9C15] hover:bg-[#FF9C15]/10 px-4 py-2 rounded-lg transition-colors"
                      >
                        + Add Filter
                      </button>
                    </div>
                  )}
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Target Platforms</h2>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { value: 'all', label: 'All', icon: '📱' },
                      { value: 'ios', label: 'iOS', icon: '🍎' },
                      { value: 'android', label: 'Android', icon: '🤖' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setTargetPlatforms([opt.value])}
                        className={`p-4 rounded-lg border-2 text-center transition-all ${
                          targetPlatforms[0] === opt.value
                            ? 'border-[#FF9C15] bg-[#FF9C15]/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <span className="text-2xl">{opt.icon}</span>
                        <p className="font-medium text-white mt-2">{opt.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Schedule */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">When to Send</h2>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <button
                      type="button"
                      onClick={() => setScheduleType('NOW')}
                      className={`p-6 rounded-xl border-2 text-center transition-all ${
                        scheduleType === 'NOW'
                          ? 'border-[#FF9C15] bg-[#FF9C15]/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <span className="text-4xl">🚀</span>
                      <p className="font-semibold text-white mt-3">Send Now</p>
                      <p className="text-sm text-gray-400 mt-1">Deliver immediately</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduleType('SCHEDULED')}
                      className={`p-6 rounded-xl border-2 text-center transition-all ${
                        scheduleType === 'SCHEDULED'
                          ? 'border-[#FF9C15] bg-[#FF9C15]/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <span className="text-4xl">📅</span>
                      <p className="font-semibold text-white mt-3">Schedule</p>
                      <p className="text-sm text-gray-400 mt-1">Pick date & time</p>
                    </button>
                  </div>

                  {scheduleType === 'SCHEDULED' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Date</label>
                        <input
                          type="date"
                          value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Time</label>
                        <input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm text-gray-400 mb-1">Timezone</label>
                        <select
                          value={scheduleTimezone}
                          onChange={(e) => setScheduleTimezone(e.target.value)}
                          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                        >
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="Europe/London">London (GMT)</option>
                          <option value="Asia/Dubai">Dubai (GST)</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {type === 'TRIGGERED' && (
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                    <h2 className="text-xl font-semibold text-white mb-4">Trigger Event</h2>
                    <select
                      value={triggerType}
                      onChange={(e) => setTriggerType(e.target.value as TriggerType)}
                      className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]"
                    >
                      <optgroup label="User Events">
                        <option value="USER_INACTIVE">User Inactive</option>
                        <option value="USER_NEW">New User Signup</option>
                      </optgroup>
                      <optgroup label="Content Events">
                        <option value="STORY_CREATED">Story Created</option>
                        <option value="STORY_EXPIRING_SOON">Story Expiring</option>
                        <option value="PING_CREATED">Ping Created</option>
                        <option value="PING_MILESTONE">Ping Milestone</option>
                        <option value="PING_TRENDING">Ping Trending</option>
                      </optgroup>
                      <optgroup label="Engagement Events">
                        <option value="DM_UNREAD_PENDING">Unread DMs</option>
                        <option value="FOLLOW_RECEIVED">New Follower</option>
                        <option value="LIKE_RECEIVED">Like Received</option>
                      </optgroup>
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Review */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Campaign Summary</h2>

                  <div className="space-y-4">
                    <div className="flex justify-between py-3 border-b border-white/10">
                      <span className="text-gray-400">Campaign Name</span>
                      <span className="text-white font-medium">{name}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-white/10">
                      <span className="text-gray-400">Type</span>
                      <span className="text-white">{type}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-white/10">
                      <span className="text-gray-400">Audience</span>
                      <span className="text-white">
                        {audienceType === 'ALL' ? 'All Users' : audienceType === 'USERS' ? 'Specific Users' : 'Segment'}
                      </span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-white/10">
                      <span className="text-gray-400">Platforms</span>
                      <span className="text-white capitalize">{targetPlatforms.join(', ')}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-white/10">
                      <span className="text-gray-400">Schedule</span>
                      <span className="text-white">
                        {scheduleType === 'NOW' ? 'Send Immediately' : `${scheduleDate} at ${scheduleTime}`}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-2xl">
                      ✓
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Ready to Launch!</h3>
                      <p className="text-gray-400 text-sm mt-1">
                        Your campaign is configured and ready to go. Click "Launch Campaign" to start sending notifications.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Preview */}
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-8">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-white">Preview</h3>
                  <div className="flex bg-slate-800 rounded-lg p-1">
                    <button
                      onClick={() => setPreviewPlatform('ios')}
                      className={`px-3 py-1 rounded text-sm transition-colors ${
                        previewPlatform === 'ios' ? 'bg-[#FF9C15] text-white' : 'text-gray-400'
                      }`}
                    >
                      iOS
                    </button>
                    <button
                      onClick={() => setPreviewPlatform('android')}
                      className={`px-3 py-1 rounded text-sm transition-colors ${
                        previewPlatform === 'android' ? 'bg-[#FF9C15] text-white' : 'text-gray-400'
                      }`}
                    >
                      Android
                    </button>
                  </div>
                </div>
                <div className="flex justify-center">
                  <DevicePreview
                    title={templateTitle}
                    body={templateBody}
                    platform={previewPlatform}
                    imageUrl={templateImageUrl}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
          <button
            type="button"
            onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : navigate('/campaigns')}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
          >
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </button>

          {currentStep < 5 ? (
            <button
              type="button"
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="px-6 py-3 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            >
              {saving ? 'Launching...' : isEditing ? 'Update Campaign' : '🚀 Launch Campaign'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignBuilder;
